import { useMemo, useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { cn } from './internal/utils'
import { collectKeys, filterTree, flattenTree, toggleChecked } from './internal/treeLogic'
import Checkbox from './Checkbox'
import Input from './Input'

const INDENT = 22

/**
 * antd Tree. Supports treeData, checkable (with parent/child propagation and
 * halfCheckedKeys), expandedKeys / defaultExpandAll, selectedKeys, showLine,
 * disabled nodes, and the DirectoryTree / TreeSelect-free subset the app uses.
 */
const Tree = ({
  treeData = [],
  checkable = false,
  selectable = true,
  checkedKeys,
  defaultCheckedKeys = [],
  onCheck,
  selectedKeys,
  defaultSelectedKeys = [],
  onSelect,
  expandedKeys,
  defaultExpandedKeys = [],
  defaultExpandAll = false,
  onExpand,
  titleRender,
  blockNode = false,
  showIcon = false,
  className,
  style,
  ...rest
}) => {
  const [innerChecked, setInnerChecked] = useState(defaultCheckedKeys)
  const [innerSelected, setInnerSelected] = useState(defaultSelectedKeys)
  const [halfChecked, setHalfChecked] = useState([])
  const [innerExpanded, setInnerExpanded] = useState(() =>
    defaultExpandAll ? collectKeys(treeData) : defaultExpandedKeys
  )

  const resolvedChecked = Array.isArray(checkedKeys)
    ? checkedKeys
    : checkedKeys?.checked ?? innerChecked
  const resolvedSelected = selectedKeys ?? innerSelected
  const resolvedExpanded = expandedKeys ?? innerExpanded

  const rows = useMemo(
    () => flattenTree(treeData, resolvedExpanded),
    [resolvedExpanded, treeData]
  )

  const toggleExpand = (key) => {
    const next = resolvedExpanded.includes(key)
      ? resolvedExpanded.filter((item) => item !== key)
      : [...resolvedExpanded, key]
    if (!expandedKeys) setInnerExpanded(next)
    onExpand?.(next, { expanded: !resolvedExpanded.includes(key) })
  }

  const handleCheck = (node, checked) => {
    const result = toggleChecked(treeData, resolvedChecked, node.key, checked)
    if (!checkedKeys) setInnerChecked(result.checked)
    setHalfChecked(result.halfChecked)
    onCheck?.(result.checked, {
      checked,
      node,
      halfCheckedKeys: result.halfChecked,
      checkedNodes: [],
    })
  }

  const handleSelect = (node) => {
    if (!selectable || node.disabled) return
    const isSelected = resolvedSelected.includes(node.key)
    const next = isSelected ? [] : [node.key]
    if (!selectedKeys) setInnerSelected(next)
    onSelect?.(next, { selected: !isSelected, node })
  }

  return (
    <div className={cn('ui-tree text-sm', className)} style={style} role="tree" {...rest}>
      {rows.map((node) => {
        const isExpanded = resolvedExpanded.includes(node.key)
        const isSelected = resolvedSelected.includes(node.key)
        const title = titleRender ? titleRender(node) : node.title

        return (
          <div
            key={String(node.key)}
            role="treeitem"
            aria-expanded={node.hasChildren ? isExpanded : undefined}
            aria-selected={isSelected}
            className={cn(
              'ui-tree-treenode flex items-center gap-1 rounded-[var(--radius-xs)] py-0.5',
              blockNode && 'w-full',
              isSelected && 'ui-tree-treenode-selected bg-[var(--gray-100)]'
            )}
            style={{ paddingLeft: node.depth * INDENT }}
          >
            {node.hasChildren ? (
              <button
                type="button"
                aria-label={isExpanded ? 'Réduire' : 'Développer'}
                onClick={() => toggleExpand(node.key)}
                className="ui-tree-switcher inline-flex h-6 w-6 items-center justify-center rounded text-[var(--text-muted)] hover:bg-[var(--gray-100)]"
              >
                {isExpanded ? (
                  <ChevronDown size={13} aria-hidden="true" />
                ) : (
                  <ChevronRight size={13} aria-hidden="true" />
                )}
              </button>
            ) : (
              <span className="ui-tree-switcher-noop inline-block h-6 w-6" aria-hidden="true" />
            )}

            {checkable ? (
              <Checkbox
                checked={resolvedChecked.includes(node.key)}
                indeterminate={halfChecked.includes(node.key)}
                disabled={node.disabled || node.disableCheckbox}
                onChange={(event) => handleCheck(node, event.target.checked)}
              />
            ) : null}

            {showIcon && node.icon ? (
              <span className="ui-tree-iconEle" aria-hidden="true">
                {node.icon}
              </span>
            ) : null}

            <button
              type="button"
              disabled={node.disabled}
              onClick={() => handleSelect(node)}
              className={cn(
                'ui-tree-node-content-wrapper min-h-[28px] flex-1 rounded px-1 text-left',
                node.disabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-[var(--gray-100)]'
              )}
            >
              {title}
            </button>
          </div>
        )
      })}
    </div>
  )
}

/** antd DirectoryTree renders the same tree with every branch expandable on click. */
const DirectoryTree = (props) => <Tree {...props} blockNode />

/** Small convenience used by permission screens: a searchable tree. */
const SearchableTree = ({ treeData, placeholder = 'Rechercher', ...rest }) => {
  const [query, setQuery] = useState('')
  const filtered = useMemo(() => {
    if (!query.trim()) return treeData
    const needle = query.trim().toLowerCase()
    return filterTree(treeData, (node) => String(node.title).toLowerCase().includes(needle))
  }, [query, treeData])

  return (
    <div className="space-y-2">
      <Input.Search
        value={query}
        placeholder={placeholder}
        onChange={(event) => setQuery(event.target.value)}
      />
      <Tree {...rest} treeData={filtered} defaultExpandAll={Boolean(query)} />
    </div>
  )
}

Tree.DirectoryTree = DirectoryTree
Tree.SearchableTree = SearchableTree

export { DirectoryTree }
export default Tree
